// src/pages/ReportAssegnati.jsx
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { authFetch } from '../utils/authFetch';
import { Card } from '../components/ui';

const API_BASE = '/api';

function formatDateList(dates = []) {
  if (!dates || dates.length === 0) return '-';
  return dates
    .map((d) =>
      new Date(d).toLocaleDateString('it-IT', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
      })
    )
    .join(', ');
}

function formatTypeLabel(type) {
  if (type === 'manutenzione') return 'INTERVENTO DI MANUTENZIONE';
  return 'INTERVENTO TECNICO';
}

const ReportAssegnati = () => {
  const navigate = useNavigate();
  const [assignedOrders, setAssignedOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const loadAssigned = async () => {
    setLoading(true);
    setError('');

    try {
      const res = await authFetch(`${API_BASE}/work/my-events`);
      if (!res.ok) {
        const data = await res.json().catch(() => null);
        throw new Error(
          data?.error || 'Errore nel recupero delle lavorazioni assegnate.'
        );
      }

      const data = await res.json();

      // Normalizzo: ogni elemento avrà SEMPRE { workOrder, dates: [] }
      const normalized = (Array.isArray(data) ? data : []).map((item) => {
        const workOrder = item.workOrder || item; // fallback nel caso il backend restituisca direttamente la workOrder
        const dates = Array.isArray(item.dates)
          ? item.dates
          : item.date
          ? [item.date]
          : [];

        return { workOrder, dates };
      });

      setAssignedOrders(normalized);
    } catch (err) {
      console.error('Errore caricamento report assegnati:', err);
      setError(err.message || 'Errore nel caricamento dei report assegnati.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadAssigned();
  }, []);

  const handleCompile = (entry) => {
    const { workOrder, dates } = entry;
    if (!workOrder) return;

    const firstDate = Array.isArray(dates) && dates.length > 0 ? dates[0] : null;

    const prefill = {
      // dati base
      tipo: workOrder.type, // 'manutenzione' | 'intervento'
      codiceInterno: workOrder.internalCode,
      codiceEsterno: workOrder.externalCode,
      cliente: workOrder.companyName,
      impianto: workOrder.equipmentPresidio,
      luogo: workOrder.place,
      modello: workOrder.equipmentModel,
      seriale: workOrder.equipmentSerial,
      inventario: workOrder.equipmentInventory,
      // per il VIT
      riferimentoChiamata: workOrder.externalCode,
      dataIntervento: firstDate ? new Date(firstDate) : null,
      telefono: workOrder.customerPhone || '',
      descrizione: workOrder.description || '',

      // 👇 STORICO NOTE E META
      noteGeneraliLog: workOrder.noteGeneraliLog || [],
      noteGenerali: workOrder.noteGenerali || '',
      iteration:
        typeof workOrder.iteration === 'number' ? workOrder.iteration : 0,
      createdAt: workOrder.createdAt || null,
    };

    const targetRoute =
      workOrder.type === 'manutenzione'
        ? '/report-assegnati/manutenzioni'
        : '/report-assegnati/vit';

    navigate(targetRoute, {
      state: {
        fromWorkOrder: prefill,
        workOrderId: workOrder._id,
      },
    });
  };

  return (
    <div className="min-h-screen bg-gray-100 py-8 px-3 md:px-6 flex justify-center">
      <div className="w-full max-w-5xl space-y-6">
        {/* Header */}
        <Card className="shadow-sm px-4 py-4 md:px-6 md:py-5 flex items-center justify-between">
          <div>
            <h1 className="text-xl md:text-2xl font-semibold text-gray-800 mb-1">
              Report assegnati
            </h1>
            <p className="text-xs md:text-sm text-gray-500">
              Seleziona una lavorazione assegnata e compila o modifica il
              relativo report di manutenzione o verbale di intervento tecnico.
            </p>
          </div>
          <button
            onClick={() => navigate('/report-interventi')}
            className="hidden md:inline-flex px-3 py-1.5 rounded-lg text-xs bg-gray-100 text-gray-700 hover:bg-gray-200"
          >
            ← Indietro
          </button>
        </Card>

        {loading ? (
          <Card className="shadow-sm p-6 text-sm text-gray-600">
            Caricamento lavorazioni assegnate...
          </Card>
        ) : error ? (
          <div className="bg-white rounded-2xl shadow-sm border border-red-200 p-6 text-sm text-red-600">
            {error}
          </div>
        ) : assignedOrders.length === 0 ? (
          <Card className="shadow-sm p-6 text-sm text-gray-600">
            Al momento non ci sono lavorazioni assegnate da compilare.
          </Card>
        ) : (
          <div className="grid gap-4 md:grid-cols-2">
            {assignedOrders.map((entry) => {
              const { workOrder, dates } = entry;
              const datesArray = Array.isArray(dates) ? dates : [];
              const firstDate =
                datesArray.length > 0 ? new Date(datesArray[0]) : null;
              const isCompiled = !!workOrder.assignedReportCompiled;

              // === codice interno con iterazione ===
              const baseCode = workOrder.internalCode || '-';
              const iter =
                typeof workOrder.iteration === 'number'
                  ? workOrder.iteration
                  : 0;
              const displayCode =
                iter > 0 ? `${baseCode}-${iter}` : baseCode;

              return (
                <Card
                  key={workOrder._id}
                  className="shadow-md p-4 flex flex-col"
                >
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
                      {formatTypeLabel(workOrder.type)}
                    </span>

                    <div className="flex items-center space-x-2">
                      {isCompiled && (
                        <span className="text-[11px] font-semibold text-red-600">
                          Già compilato
                        </span>
                      )}
                      <span className="text-xs px-2 py-1 rounded-full bg-gray-100 text-gray-700">
                        {displayCode}
                      </span>
                    </div>
                  </div>

                  <h2 className="text-sm font-semibold text-gray-800 mb-1">
                    {workOrder.companyName || 'Cliente non specificato'}
                  </h2>

                  <p className="text-xs text-gray-500 mb-1">
                    {workOrder.place || 'Indirizzo non specificato'}
                  </p>

                  <div className="text-[11px] text-gray-500 mb-2 space-y-0.5">
                    <p>
                      <span className="font-semibold">Modello:</span>{' '}
                      {workOrder.equipmentModel || '-'}
                    </p>
                    <p>
                      <span className="font-semibold">Seriale:</span>{' '}
                      {workOrder.equipmentSerial || '-'}
                    </p>
                    <p>
                      <span className="font-semibold">Reparto / Presidio:</span>{' '}
                      {workOrder.equipmentPresidio || '-'}
                    </p>
                    <p>
                      <span className="font-semibold">Inventario:</span>{' '}
                      {workOrder.equipmentInventory || '-'}
                    </p>
                    <p>
                      <span className="font-semibold">Date intervento:</span>{' '}
                      {formatDateList(datesArray)}
                    </p>
                  </div>

                  {workOrder.description && (
                    <p className="text-[11px] text-gray-500 mb-3 line-clamp-3">
                      <span className="font-semibold">Descrizione: </span>
                      {workOrder.description}
                    </p>
                  )}

                  <div className="mt-auto pt-2 flex items-center justify-between">
                    {firstDate && (
                      <span className="text-[11px] text-gray-400">
                        Prossimo intervento:{' '}
                        {firstDate.toLocaleDateString('it-IT', {
                          day: '2-digit',
                          month: '2-digit',
                          year: 'numeric',
                        })}
                      </span>
                    )}
                    <button
                      type="button"
                      onClick={() => handleCompile(entry)}
                      className={`ml-2 px-3 py-1.5 rounded-lg text-xs font-medium transition ${
                        isCompiled
                          ? 'bg-amber-500 hover:bg-amber-600 text-white'
                          : 'bg-indigo-600 hover:bg-indigo-700 text-white'
                      }`}
                    >
                      {isCompiled
                        ? `Modifica ${displayCode}`
                        : `Compila ${displayCode}`}
                    </button>
                  </div>
                </Card>
              );
            })}
          </div>
        )}

        <div className="md:hidden flex justify-center">
          <button
            onClick={() => navigate('/report-interventi')}
            className="mt-2 px-4 py-2 rounded-lg bg-gray-100 text-gray-700 text-xs font-medium hover:bg-gray-200"
          >
            ← Indietro
          </button>
        </div>
      </div>
    </div>
  );
};

export default ReportAssegnati;
